import { type ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spacing16 } from 'autogen/design-tokens/tokens';
import {
    AcceptButton,
    CheckboxDescription,
    CheckboxLabel,
    DismissButton,
    Headline,
    Section1,
    Section2,
    Section2LinkText
} from 'autogen/translation-keys/trans-website-comai-termsandconditions';
import { Button } from 'components/Button';
import DefaultContentRenderer from 'components/generic/Drawer/components/DefaultContentRenderer';
import DefaultFooterRenderer from 'components/generic/Drawer/components/DefaultFooterRenderer';
import DrawerView from 'components/generic/Drawer/DrawerView';
import useDrawer, { closeDrawer } from 'components/generic/Drawer/hooks/useDrawer';
import InlineLinkActionButton from 'components/generic/InlineButton/InlineLinkActionButton';
import { Typography } from 'components/generic/typography';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';
import Checkbox from 'components/shared/inputs/checkbox/Checkbox';
import format from 'helpers/translations';
import { COM_AI_DRAWER_ID, COM_AI_TERMS_DRAWER_ID } from '../shared/consts';
import useComAIStore from '../store/comAIStore';

type FeedBackDrawerProps = {
    drawerId: string;
};

const Wrapper = styled.div`
    flex: 1 1 auto;
    padding: ${({ theme }) => theme.Spacing20} ${({ theme }) => theme.Spacing16};
    padding-top: 0;
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.Spacing16};
`;

const StyledHeadline = styled(Typography.Headline)`
    text-align: center;
`;

const Footer = styled(DefaultFooterRenderer)`
    border-top: 1px solid ${({ theme }) => theme.ColorBorderSecondary};
`;

const Section2Link = ({ children }) => {
    return (
        <InlineLinkActionButton
            action={{
                url: 'https://www.matas.dk/privatlivspolitik',
                $type: 'PlainLinkAction',
                openInNewWindow: true,
                noFollow: false
            }}
        >
            {children}
        </InlineLinkActionButton>
    );
};

const TermsAndConditionsDrawer: React.FC<FeedBackDrawerProps> = ({ drawerId }) => {
    const { t } = useTranslation();
    const comAIDrawer = useDrawer(COM_AI_DRAWER_ID, { disableOverlay: true });
    const setHasAcceptedTerms = useComAIStore((state) => state.setHasAcceptedTerms);
    const [userAcceptedTerms, setUserAcceptedTerms] = useState(false);

    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        setUserAcceptedTerms(event.target.checked);
    };

    const acceptCookies = () => {
        if (!userAcceptedTerms) return;

        setHasAcceptedTerms(true);
        comAIDrawer.open();
        closeDrawer(COM_AI_TERMS_DRAWER_ID);
    };
    const dismiss = () => {
        setUserAcceptedTerms(false);
        closeDrawer(COM_AI_TERMS_DRAWER_ID);
    };

    return (
        <DrawerView
            id={drawerId}
            position="right"
            fullscreen
            contentRenderer={(props) => <DefaultContentRenderer.NoPadding {...props} />}
            footerRenderer={(props) => (
                <Footer {...props}>
                    <FlexColumn justifyContent="space-between">
                        <Button variant="primary" size="lg" onClick={acceptCookies} disabled={!userAcceptedTerms}>
                            {t(AcceptButton)}
                        </Button>
                        <Button variant="ghost" size="lg" onClick={dismiss}>
                            {t(DismissButton)}
                        </Button>
                    </FlexColumn>
                </Footer>
            )}
        >
            <Wrapper>
                <FlexColumn gap={Spacing16}>
                    <StyledHeadline size="XL">{t(Headline)}</StyledHeadline>
                    <Typography.Body strong>{t(CheckboxLabel)}</Typography.Body>
                    <Checkbox
                        label={t(CheckboxDescription)}
                        name="consent"
                        onChange={onChange}
                        checked={userAcceptedTerms}
                    />
                    <Typography.Body>{t(Section1)}</Typography.Body>
                    <Typography.Body>
                        {format(t(Section2), <Section2Link>{t(Section2LinkText)}</Section2Link>)}
                    </Typography.Body>
                </FlexColumn>
            </Wrapper>
        </DrawerView>
    );
};

export default TermsAndConditionsDrawer;
